import {
  ApplicationCategorySelectedEventType,
  ApplicationNavigationEventType,
  ApplicationSearchEventSource,
  ApplicationSearchInteractionType,
  ApplicationSuggestionSelectedEventType,
  PageNameTypes,
} from '~shared/modules/application-event/constants';
import { ApplicationEvent } from '~shared/modules/application-event/models';
import * as gaTracking from '~shared/modules/ga-tracking/models';
import { ContentBannerPayload, EventWithLabel } from '~shared/modules/seo/constants';
import { GATrackingApplicationEventMapperFunctionGenerator } from './ga-tracking-application-event-mapper-function-generator.util';
import { GaTrackingEventName, GATrackingEventType } from '~shared/modules/ga-tracking/constants';

// PLEASE ADD FUNCTIONS IN THIS FORM, map[ApplicationEventType name]Event
// As an example: to map ApplicationEventType.AddToCart event
// The function name should be `mapAddToCartEvent` and WE SHOULD ALWAYS PASS payload
namespace GATrackingApplicationEventMapperFunctions {
  export function mapScrollingEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingScrollingEvent(payload);
  }

  export function mapBannerInteractionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingBannerEvent(payload);
  }

  export function mapCookieBannerEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingCookieBannerEvent(payload);
  }

  export function mapCarouselClickEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingCarouselClickEvent(payload);
  }

  export function mapCarouselButtonEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingCarouselButtonEvent(payload);
  }

  export function mapPdpImageInteractionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPdpImageInteractionEvent(payload);
  }

  export function mapPdpDescriptionInteractionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPdpDescriptionInteractionEvent(payload);
  }

  export function mapPdpFurtherInformationInteractionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPdpFurtherInformationInteractionEvent(payload);
  }

  export function mapPdpLoadbeeInteractionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPdpLoadbeeniteractionEvent(payload);
  }

  export function mapPdpCompareAllOffersClickEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPdpCompareAllOffersinteractionEvent(payload);
  }

  export function mapOutOfStockNotifyClickEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingOutOfStockInteractionEvent(payload);
  }

  export function mapExploreSimilarProductsClickEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingExploreSimilarProductsInteractionEvent(payload);
  }

  export function mapPdpBrandInteractionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPdpBrandInteractionEvent(payload);
  }

  export function mapPdpSellerInteractionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPdpSellerInteractionEvent(payload);
  }

  export function mapPdpOtherOffersInteractionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPdpOtherOffersClickEvent(payload);
  }

  export function mapSeoFaqToggleEvent(payload: EventWithLabel): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingSeoFaqToggleEvent(payload);
  }

  export function mapSimilarProductsViewEvent(payload: any): gaTracking.GATrackingEvent {
    const { products, pageName, index } = payload;
    return new gaTracking.GATrackingSimilarProductsViewEvent(products, pageName, index);
  }

  export function mapLoadBeeButtonViewEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingLoadbeeButtonViewEvent(payload);
  }

  export function mapAddToCartEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingAddToCartEvent(
      payload.product,
      {
        offer: payload?.offerId,
        quantity: payload.quantity,
        productId: payload.productId,
        accountType: payload.accountType,
      },
      payload.priceType,
      payload.breadcrumbs,
      payload.recommendation,
      payload.EECLabel,
      payload.isBlackDealsEnabled,
      payload.isVasChecked,
      payload.servicePrice
    );
  }

  export function mapAddToCartGa4Event(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingAddToCartGa4Event(
      payload.product,
      {
        offer: payload?.offerId,
        quantity: payload.quantity,
        productId: payload.productId,
        accountType: payload.accountType,
        valueAddedServices: payload.valueAddedServices,
        isVasChecked: payload.isVasChecked,
      },
      payload.priceType,
      payload.breadcrumbs,
      payload.recommendation,
      payload.EECLabel,
      payload.previousUrl,
      payload.isBlackDealsEnabled,
      payload.searchTerm,
      payload.itemListName
    );
  }

  export function mapAddToCartMoreThanInStockEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingAddToCartMoreThanInStockInteractionEvent(payload.productName);
  }

  export function mapOrderHistoryPDPLinkEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingOrderHistoryPDPLinkEvent(payload.event, payload.category, payload.label);
  }

  export function mapRegistrationTypeEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingRegistrationTypeClickEvent(
      payload.event,
      payload.category,
      payload.label,
      payload.action
    );
  }

  export function mapRegisterWithEmailEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingRegisterWithEmailButtonClickEvent(
      payload.event,
      payload.category,
      payload.label,
      payload.action
    );
  }

  export function mapProceedToCheckoutEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingProceedToCheckoutClickEvent(
      payload.event,
      payload.category,
      payload.action,
      payload.label
    );
  }

  export function mapAuthorizationEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingAuthorizationEvent(
      payload.event,
      payload.category,
      payload.action,
      payload.label,
      payload.horecaTag
    );
  }

  export function mapIdamRegisterEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingIdamRegisterEvent(payload.event, payload.category, payload.action, payload.label);
  }

  export function mapSignUpToCheckoutTransitionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingSignupCheckoutErrorEventModel(payload.category, payload.action, payload.label);
  }

  export function mapPriceTypeChangedEvent(payload: any) {
    return new gaTracking.GATrackingPriceTypeChangeEventModel(payload.priceType, payload.location);
  }

  export function mapButtonClickEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingButtonClickEvent(payload.event, payload.category, payload.action, payload.label);
  }

  export function mapCarouselItemsViewedEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPromoViewEvent(payload.item, payload.index, payload.categoryName, payload.label);
  }

  export function mapCartUpdateEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingCartUpdateEvent(payload.offerLines, payload.recommendation);
  }

  export function mapCategorySelectedEvent(payload: any): gaTracking.GATrackingEvent {
    const type: ApplicationCategorySelectedEventType = payload.type;

    if (type === ApplicationCategorySelectedEventType.CategoryImage) {
      return new gaTracking.GATrackingCategoryImageClickEvent(payload.categoryName);
    }

    if (type === ApplicationCategorySelectedEventType.CategoryFilterPLP) {
      return new gaTracking.GATrackingPlpCategoryFilterClickEvent(payload.categoryName);
    }

    return null;
  }

  export function mapCheckoutErrorsEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingCheckoutErrorsEvent(payload.errors);
  }

  export function mapCheckoutStepEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingCheckoutEvent(payload.validOffers, payload.actionField);
  }

  export function mapCheckoutStepperEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingCheckoutFunnelEventModel(payload.label);
  }

  export function mapCreateAccountEvent(payload: any) {
    return new gaTracking.GaTrackingCreateAccountEvent(payload.event, payload.category);
  }

  export function mapCreateAccountExperimentEvent(payload: any) {
    return new gaTracking.GaTrackingCreateAccountExperimentEvent(payload.event, payload.category);
  }

  export function mapBannerClickExperimentEvent(payload: any) {
    return new gaTracking.GaTrackingBannerClickExperimentEvent(payload.event);
  }

  export function mapLogoutClickEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingLogoutEvent();
  }

  export function mapNavigationEvent(payload: any): gaTracking.GATrackingEvent {
    const type: ApplicationNavigationEventType = payload.type;
    switch (type) {
      case ApplicationNavigationEventType.Breadcrumb:
        return new gaTracking.GATrackingBreadcrumbEvent(payload.title);
      case ApplicationNavigationEventType.FooterNavigationClick:
        return new gaTracking.GATrackingFooterNavigationEvent(payload.path, payload.title);
      case ApplicationNavigationEventType.NavigationBarClick:
        return new gaTracking.GATrackingNavigationBarClickEvent(payload.title);
      case ApplicationNavigationEventType.NavigationUserAccountMenuClick:
        return new gaTracking.GATrackingUserAccountMenuClickEvent(payload.title);
      case ApplicationNavigationEventType.CheckoutStepNavigationClick:
        return new gaTracking.GaTrackingCheckoutStepNavigationEvent(payload.title, payload.additionalData);
      case ApplicationNavigationEventType.PLPCategoryNavigation:
        return new gaTracking.GATrackingPLPCategoryNavigationEvent(payload.additionalData.categoryName);
      case ApplicationNavigationEventType.PLPCategoryNavigationExpanded:
        return new gaTracking.GATrackingPLPCategoryExpandedNavigationEvent(payload.additionalData.showAllText);
      case ApplicationNavigationEventType.GlobalHeaderClick:
        return new gaTracking.GATrackingGlobalHeaderClickEvent(payload);
      case ApplicationNavigationEventType.GlobalFooterClick:
        return new gaTracking.GATrackingGlobalFooterClickEvent(payload);
      case ApplicationNavigationEventType.TopNavigationClick:
      default:
        return new gaTracking.GATrackingTopNavigationEvent(payload.path, payload.title);
    }
  }

  export function mapNotificationEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingNotificationEvent(payload.section, payload.title);
  }

  export function mapOptimizeEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingOptimizeEvent(payload.event, payload.category);
  }

  export function mapPaymentFailureEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPaymentFailureEvent(payload.title, payload.payment);
  }

  export function mapProductClickEvent(payload: any): gaTracking.GATrackingEvent {
    const { pageName, product, index, url, priceType, isBlackDealsEnabled, searchTerm, label, triggerPromotionEvent } =
      payload;

    if (pageName === PageNameTypes.SimilarProducts) {
      return new gaTracking.GATrackingSimilarProductClickEvent(product, priceType, index);
    }

    const isAlternativeProduct = [
      PageNameTypes.FilterAlternativeProducts,
      PageNameTypes.CategoryAlternativeProducts,
      PageNameTypes.PromotionAlternativeProducts,
      PageNameTypes.ExpandedSearchAlternativeProducts,
      PageNameTypes.MostPopularProducts,
      PageNameTypes.MostRelevantProducts,
      PageNameTypes.ParsedTextProducts,
    ];

    if (isAlternativeProduct.includes(pageName)) {
      return new gaTracking.GATRackingAlternativeProductClickEvent(product, pageName);
    }

    if (triggerPromotionEvent && product.hasPromotion) {
      return new gaTracking.GATrackingPlpPromotionClickEvent(product, pageName, index, searchTerm, label);
    }

    return new gaTracking.GATrackingProductClickEvent(
      url,
      product,
      priceType,
      pageName,
      index,
      isBlackDealsEnabled,
      searchTerm
    );
  }

  export function mapProductDetailsViewedEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingProductDetailsViewEvent(
      payload.product,
      payload.priceType,
      payload.isBlackDealsEnabled,
      payload.selectedOfferId,
      payload.breadcrumbs,
      payload.isProductHasLoadbee,
      payload.EECLabel,
      payload.recommendation
    );
  }

  export function mapViewItemEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingViewItemEvent(
      payload.product,
      payload.priceType,
      payload.selectedOfferId,
      payload.breadcrumbs,
      payload.EECLabel,
      payload.recommendation,
      payload.previousUrl,
      payload.isBlackDealsEnabled
    );
  }

  export function mapVolumePriceViewedEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingVolumePriceSelectedEvent();
  }

  export function mapVolumePriceTableClickedEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingVolumePriceTableClickedEvent(payload.label);
  }

  export function mapQuantityPickerInteractionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingBuyboxQuantityPickerInteractionEvent(payload.label);
  }
  export function mapViewItemsListEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingViewItemListEventModel(payload);
  }

  export function mapViewPromotionEvent(payload: any): gaTracking.GATrackingEvent {
    const { products, creative_name, item_list_name, priceType } = payload;
    return new gaTracking.GaTrackingViewPromotionEventModel(products, creative_name, item_list_name, priceType);
  }

  export function mapZRPCategoryClickedEvent(payload: any): gaTracking.GATrackingEvent {
    const { categoryName, pageTitle } = payload;
    return new gaTracking.GATrackingZRPCategoryClickedApplicationEvent(categoryName, pageTitle);
  }

  export function mapSelectItemEvent(payload: any): gaTracking.GATrackingEvent {
    const {
      pageName,
      product,
      priceType,
      productIndex,
      triggerPromotionEvent,
      creative_name,
      colorVariantId,
      search_term,
    } = payload;
    if (triggerPromotionEvent) {
      return new gaTracking.GaTrackingSelectPromotionEventModel(
        pageName,
        product,
        priceType,
        productIndex,
        creative_name,
        search_term
      );
    }

    return new gaTracking.GaTrackingSelectItemEventModel(
      pageName,
      product,
      priceType,
      productIndex,
      colorVariantId,
      search_term
    );
  }

  export function mapAddPaymentInfoEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingAddPaymentInfoEventModel(payload);
  }

  export function mapAddShippingInfoEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingAddShippingInfoEventModel(payload);
  }

  export function mapReviewOrderInfoEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingAddReviewInfoEventModel(payload);
  }

  export function mapAlternativeProductsShowEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingAlternativeProductsShowEvent(payload.pageName);
  }

  export function mapPurchaseEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPurchaseEvent(
      payload.offerLines,
      payload.totals,
      payload.orderPayment,
      payload.price_type
    );
  }

  export function mapPurchaseGA4Event(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPurchaseGA4Event(
      payload.offerLines,
      payload.totals,
      payload.orderPayment,
      payload.price_type,
      payload.payment_method,
      payload.currency,
      payload.cartFeatures
    );
  }

  export function mapRemoveFromCartEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingRemoveFromCartEvent({
      id: payload.productId,
      offerLine: payload.offerLine,
      removedItem: payload.removedItem,
    });
  }

  export function mapCategoryPageEvent(payload: any): gaTracking.GATrackingEvent {
    const source: ApplicationSearchEventSource = payload.source;

    if (source === ApplicationSearchEventSource.ZeroResult) {
      return new gaTracking.GaTrackingCategoryPageZeroResult(payload);
    } else if (source === ApplicationSearchEventSource.Filter) {
      return new gaTracking.GATrackingFilterPLPsEvent(
        payload.GA360_query,
        payload.content,
        GATrackingEventType.FilteredCategoryPage,
        GaTrackingEventName.CategoryPageFiltered
      );
    } else if (
      [
        ApplicationSearchEventSource.FilterUnselected,
        ApplicationSearchEventSource.FilterRemove,
        ApplicationSearchEventSource.FilterRemoveAll,
      ].includes(source)
    ) {
      return new gaTracking.GATrackingRemoveFilterPLPsEvent(
        source,
        payload.GA360_query,
        GATrackingEventType.RemoveFilteredCategoryPage
      );
    }

    return new gaTracking.GATrackingCategoryPageEvent(payload);
  }

  export function mapCategoryPageViewedEvent(payload: any): gaTracking.GATrackingEvent {
    const source: ApplicationSearchEventSource = payload.source;

    if (source === ApplicationSearchEventSource.ZeroResult) {
      return new gaTracking.GaTrackingCategoryPageZeroResult(payload);
    } else if (source === ApplicationSearchEventSource.Filter) {
      return new gaTracking.GATrackingFilterPLPsEvent(
        payload.GA360_query,
        payload.content,
        GATrackingEventType.FilteredCategoryPage,
        GaTrackingEventName.CategoryPageFiltered
      );
    } else if (
      [
        ApplicationSearchEventSource.FilterUnselected,
        ApplicationSearchEventSource.FilterRemove,
        ApplicationSearchEventSource.FilterRemoveAll,
      ].includes(source)
    ) {
      return new gaTracking.GATrackingRemoveFilterPLPsEvent(
        source,
        payload.GA360_query,
        GATrackingEventType.RemoveFilteredCategoryPage
      );
    }

    return new gaTracking.GATrackingCategoryPageViewedEvent(payload);
  }

  export function mapSearchEvent(payload: any): gaTracking.GATrackingEvent {
    const source: ApplicationSearchEventSource = payload.source;

    if (source === ApplicationSearchEventSource.ZeroResult) {
      return new gaTracking.GaTrackingSearchZeroResult(payload);
    } else if (source === ApplicationSearchEventSource.AutoCorrection) {
      return new gaTracking.GATrackingAutoCorrectedSearchEvent(payload.GA360_query, payload.GA360_autoCorrectedQuery);
    } else if (source === ApplicationSearchEventSource.Filter) {
      return new gaTracking.GATrackingFilterPLPsEvent(
        payload.GA360_query,
        payload.content,
        GATrackingEventType.FilteredSearch,
        GaTrackingEventName.SearchFiltered
      );
    } else if (
      [
        ApplicationSearchEventSource.FilterUnselected,
        ApplicationSearchEventSource.FilterRemove,
        ApplicationSearchEventSource.FilterRemoveAll,
      ].includes(source)
    ) {
      return new gaTracking.GATrackingRemoveFilterPLPsEvent(
        source,
        payload.GA360_query,
        GATrackingEventType.RemoveFilteredSearch
      );
    }

    return new gaTracking.GATrackingSearchEvent(payload);
  }

  export function mapSearchInteractionEvent(payload: any): gaTracking.GATrackingEvent {
    const type: ApplicationSearchInteractionType = payload.type;
    return new gaTracking.GATrackingSearchInteractionEvent(type);
  }

  export function mapSuggestionSelectedEvent(payload: any): gaTracking.GATrackingEvent {
    const suggestionType: ApplicationSuggestionSelectedEventType = payload.type;
    let suggestionTerm = null;
    switch (suggestionType) {
      case ApplicationSuggestionSelectedEventType.CategorySuggestionSelected:
        suggestionTerm = payload.selectedCategory;
        break;
      case ApplicationSuggestionSelectedEventType.AllPromoSuggestionSelected:
      case ApplicationSuggestionSelectedEventType.HistorySuggestionSelected:
      case ApplicationSuggestionSelectedEventType.TextSuggestionSelected:
        suggestionTerm = payload.selectedSuggestionText;
        break;
      case ApplicationSuggestionSelectedEventType.PromoSuggestionSelected:
        suggestionTerm = payload.selectedPromotionName;
        break;
    }

    return new gaTracking.GATrackingSearchSuggestion(suggestionType, suggestionTerm);
  }

  export function mapProductsSortedEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingProductSortedEvent(payload.sortOption);
  }

  export function mapPromoImageLinkClickEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPromoImageLinkClickEvent(payload);
  }

  export function mapPLPPaginationShowMoreClickEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPLPPaginationClickEvent(payload.pagination, payload.defaultLimit);
  }

  export function mapPLPSeoTextEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPLPSeoTextViewEvent(payload);
  }

  export function mapCartLinkClickEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingCartLinkClickEvent(payload);
  }

  export function mapOrderHistoryInteractionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingOrderHistoryInteractionEvent(payload);
  }

  export function mapFilterAreaInteractionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingFilterAreaInteractionEvent(payload.type);
  }

  export function mapPLPPriceFilterInteractionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPLPPriceFilterInteractionEvent(payload.type);
  }

  export function mapPDPRegisterAsBusinessInteractionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPDPRegisterAsBusinessInteractionEvent(payload);
  }

  export function mapMiniCartInteractionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingMiniCartInteractionEvent(payload);
  }

  export function mapCarouselSwipeInteractionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingCarouselSwipeInteractionEvent(payload);
  }

  export function mapOfferPriceSyncIssueEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPDPOfferPriceSyncIssueEvent(payload);
  }

  export function mapNewsletterSubscriptionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingNewsletterSubscriptionEvent(payload);
  }

  export function mapPageViewEvent(): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPageViewEvent();
  }

  export function mapHubPageCardClickEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingHubPageCardClickEvent(payload);
  }

  export function mapFeatureFlagEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingFeatureFlagEvent(payload.event, payload.category, payload.action, payload.label);
  }

  export function mapGA4GeneralEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingGA4GeneralEvent(payload);
  }

  export function mapFlixMediaContentViewedEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingFlixMediaContentViewedEvent(
      payload.type,
      payload.event,
      payload.category,
      payload.action,
      payload.label,
      payload.gtmCustom
    );
  }

  export function mapFlixMediaContentScrollEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingFlixMediaContentScrolledEvent(
      payload.type,
      payload.event,
      payload.category,
      payload.action,
      payload.label,
      payload.gtmCustom
    );
  }

  export function mapFlixMediaFailedEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingFlixMediaFailedEvent(
      payload.type,
      payload.event,
      payload.category,
      payload.action,
      payload.label,
      payload.gtmCustom
    );
  }

  export function mapFlixMediaContentFailedEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingFlixMediaContentFailedEvent(
      payload.type,
      payload.event,
      payload.category,
      payload.action,
      payload.label,
      payload.gtmCustom
    );
  }

  export function mapFlixMediaBrandPageViewEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingFlixMediaBrandPageViewEventModel(
      payload.type,
      payload.event,
      payload.category,
      payload.action,
      payload.label,
      payload.gtmCustom
    );
  }

  export function mapPdpOfferMismatchEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingPdpOfferMismatchEvent(payload);
  }

  export function mapPDPFoodCategoryLinkEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingPdpFoodCategoryLinkEvent(payload);
  }

  export function mapOptimizelyActivateExperimentEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingOptimizelyExperimentNewEvent(
      payload.method,
      payload.event,
      payload.action,
      payload.label,
      payload.category
    );
  }

  export function mapOrderHistorySideMenuInteractionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingOrderHistorySideMenuInteractionEvent(
      payload.event,
      payload.action,
      payload.label,
      payload.category
    );
  }

  export function mapCustomLinkedGridEvent(payload: EventWithLabel): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingCustomLinkedGridClickEvent(payload);
  }

  export function mapEditorialLinkNavigationClickEvent(payload: EventWithLabel): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingEditorialLinkNavigationClickEvent(payload.label);
  }

  export function mapPdpWeeMoreDetailsClickEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingPdpWeeMoreDetailsClickEvent(payload);
  }

  export function mapSearchBannerEvent(payload): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingSearchBannerEvent(payload);
  }

  export function mapContentCardClickEvent(payload: EventWithLabel): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingContentCardClickEvent(payload);
  }

  export function mapViewCartEvent(payload): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingViewCartEventModel(payload);
  }

  export function mapRemoveItemFromCartEvent(payload): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingRemoveItemFromCartEvent(payload);
  }

  export function mapBeginCheckoutEvent(payload): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingBeginCheckoutEvent(payload);
  }

  export function mapAssistedSalesEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingAssistedSalesEvent(
      payload.event,
      payload.category,
      payload.action,
      payload.label,
      payload.employeeId,
      payload.sharedPaymentLinkId
    );
  }

  export function mapViewRecommendationEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingViewRecommendationEvent(payload.item, payload.index);
  }

  export function mapInstallationServicesEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingInstallationServicesEvent(payload.index);
  }

  export function mapInstallationServicesDetailsEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingInstallationServicesDetailsEvent();
  }

  export function mapInstallationServicesDetailsLinksEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingInstallationServicesDetailsLinksEvent(payload.item);
  }

  export function mapSelectRecommendationEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingSelectRecommendationEvent(payload.item, payload.index);
  }

  export function mapShowTooltipValueAddedServiceEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingShowTooltipValueAddedServiceEvent(payload);
  }

  export function mapCallbackClickEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GATrackingCallbackClickEvent(payload);
  }

  export function mapInvalidOfferForRegionEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingInvalidOfferForRegionEvent(payload);
  }

  export function mapChangeDeliveryAddressEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingChangeDeliveryAddressEvent(payload);
  }

  export function mapBackToCartEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingBackToCartEvent(payload);
  }

  export function mapSearch_0_resultsEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingSearch_0_resultsEventModel(payload);
  }

  export function mapSearch_0_results_clickEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingSearch_0_results_clickEventModel(payload);
  }

  export function mapSearch_ZRP_category_viewEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingPopularCategoryViewedEventModel(payload);
  }

  export function mapSearch_ZRP_other_bucketsEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingZRPOtherBucketsViewedEventModel(payload);
  }

  export function mapSearch_ZRP_other_buckets_clickedEvent(payload: any): gaTracking.GATrackingEvent {
    return new gaTracking.GaTrackingZRPOtherBucketsClickedEventModel(payload);
  }
}

export namespace GATrackingApplicationEventMapperUtil {
  export function map(event: ApplicationEvent): gaTracking.GATrackingEvent | gaTracking.GATrackingEvent[] {
    const functionName = GATrackingApplicationEventMapperFunctionGenerator.generateMapFunctionName(event.type);
    const func = GATrackingApplicationEventMapperFunctions[functionName];
    if (!func) {
      return null;
    }
    return func(event.getPayload());
  }
}
